import type { RPCQueryResult } from "eddev/hooks"
import type { Spektrix } from "../../server/lib/spektrix-types"

export type TicketPricing = RPCQueryResult<"api.ticketing.ticketPricing">
export type TicketSelection = Record<string, number>

export function eventTickets(config: TicketPricing, tickets: Spektrix.Ticket[]) {
  return tickets.filter((ticket) => ticket.event.id === config.eventId)
}

export function existingTicketSelection(config: TicketPricing, tickets: Spektrix.Ticket[]): TicketSelection {
  const prices = selectableTicketPrices(config)
  const selection: TicketSelection = {}
  for (const ticket of eventTickets(config, tickets)) {
    const price =
      prices.find((price) => price.ticketType.id === ticket.type.id && price.priceBand.id === ticket.band?.id) ??
      prices.find((price) => price.ticketType.id === ticket.type.id)
    if (price) selection[price.id] = (selection[price.id] ?? 0) + 1
  }
  return selection
}

export function selectableTicketPrices(config: TicketPricing) {
  if (config.plan.type !== "Unreserved" || config.plan.areas.length > 0) return []
  const bands = new Set(config.status.bands.map((band) => band.id))
  return config.priceList.prices
    .filter((price) => bands.has(price.priceBand.id))
    .sort((a, b) => Number(b.isBandDefault) - Number(a.isBandDefault))
}

export function selectionSummary(
  config: TicketPricing,
  selection: TicketSelection,
  heldTickets = 0,
  maxTickets: number | null = null,
  allowEmpty = false,
) {
  const prices = selectableTicketPrices(config)
  const byId = new Map(prices.map((price) => [price.id, price]))
  let totalQuantity = 0
  let totalCents = 0
  let validityWarning: string | null = null

  const items = [] as { label: string; quantity: number }[]

  for (const [id, quantity] of Object.entries(selection)) {
    const item = byId.get(id)
    if (!Number.isSafeInteger(quantity) || quantity < 0 || !byId.has(id)) {
      validityWarning = "Please choose valid ticket quantities."
      continue
    }
    totalQuantity += quantity
    totalCents += Math.round(item!.amount * 100) * quantity
    if (quantity > 0) {
      items.push({
        label: item?.ticketType.name!,
        quantity: quantity,
      })
    }
  }

  if (maxTickets !== null && totalQuantity > maxTickets) {
    validityWarning = `You may only have ${maxTickets} tickets for this session in your cart at any one time.`
  } else if (totalQuantity > config.status.available + heldTickets) {
    validityWarning = "There are not enough tickets available for this selection."
  }

  return {
    totalPrice: totalCents / 100,
    totalQuantity,
    isValidSelection:
      (totalQuantity > 0 || allowEmpty) && !validityWarning && config.instance.isOnSale && !config.instance.cancelled,
    validityWarning,
    items,
  }
}
